<template>
  <div class="pft-debug">
    <el-dialog v-model="dialogVisible" title="调试信息" width="500px" :show-close="true" :append-to-body="true" destroy-on-close>
      <div class="debug-content">
        <div class="debug-section">
          <h3>浏览器信息</h3>
          <div class="debug-item">
            <span class="label">分辨率:</span>
            <span class="value">{{ resolution }}</span>
          </div>
          <div class="debug-item">
            <span class="label">DPI:</span>
            <span class="value">{{ dpi }}</span>
          </div>
          <div class="debug-item">
            <span class="label">User Agent:</span>
            <span class="value">{{ userAgent }}</span>
          </div>
          <div class="debug-item">
            <span class="label">平台:</span>
            <span class="value">{{ platform }}</span>
          </div>
        </div>

        <div v-if="customData && Object.keys(customData).length > 0" class="debug-section">
          <h3>应用信息</h3>
          <div v-for="(value, key) in customData" :key="key" class="debug-item">
            <span class="label">{{ key }}:</span>
            <span class="value">{{ typeof value === "object" ? JSON.stringify(value) : value }}</span>
          </div>
        </div>
      </div>

      <template #footer>
        <div class="debug-footer">
          <el-button @click="dialogVisible = false">关闭</el-button>
          <el-button type="primary" ref="copyBtn">复制信息</el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted, onUnmounted, reactive, computed, watch } from "vue"
import { ElDialog, ElButton, ElMessage } from "element-plus"
import ClipboardJS from "clipboard"

interface DebugProps {
  customData?: Record<string, any>
}

const copyBtn = ref<InstanceType<typeof ElButton> | null>(null)

const props = withDefaults(defineProps<DebugProps>(), {
  customData: () => ({}),
})

// 内部状态
const dialogVisible = ref(false)
const internalCustomData = reactive<Record<string, any>>({})

// 浏览器信息
const resolution = computed(() => {
  return `${window.innerWidth} x ${window.innerHeight} (CSS像素)` + ` / ${window.screen.width} x ${window.screen.height} (物理像素)`
})

const dpi = computed(() => {
  return window.devicePixelRatio
})

const userAgent = computed(() => {
  return navigator.userAgent
})

const platform = computed(() => {
  return navigator.platform
})

// 合并props传入的customData和动态添加的数据
const customData = computed(() => {
  return {
    ...props.customData,
    ...internalCustomData,
  }
})

// 监听Ctrl+I快捷键打开调试窗口
function handleKeyDown(event: KeyboardEvent) {
  console.log("handleKeyDown", event)
  if (event.ctrlKey && event.key === "i") {
    event.preventDefault()
    dialogVisible.value = true
  }
}

const initCopy = watch(
  () => copyBtn.value,
  newVal => {
    if (newVal) {
      console.log("initCopy", newVal)
      const a = new ClipboardJS(newVal.ref as Element, {
        text: () => {
          const text = `
浏览器信息:
- 分辨率: ${resolution.value}
- DPI: ${dpi.value}
- User Agent: ${userAgent.value}
- 平台: ${platform.value}

${
  Object.keys(customData.value).length > 0
    ? `应用数据:\n${Object.entries(customData.value)
        .map(([key, value]) => `- ${key}: ${typeof value === "object" ? JSON.stringify(value) : value}`)
        .join("\n")}`
    : ""
}
  `.trim()
          return text
        },
      })
      a.on("success", () => {
        ElMessage.success("信息已复制到剪贴板")
      })
      a.on("error", () => {
        ElMessage.error("复制失败，请检查浏览器权限")
      })
      initCopy()
    }
  },
)

// 添加自定义数据的方法
function addCustomData(key: string, value: any) {
  internalCustomData[key] = value
}

// 清除自定义数据
function clearCustomData() {
  Object.keys(internalCustomData).forEach(key => {
    delete internalCustomData[key]
  })
}

// 开关调试窗口
function toggleDebugDialog(visible?: boolean) {
  if (visible !== undefined) {
    dialogVisible.value = visible
  } else {
    dialogVisible.value = !dialogVisible.value
  }
}

// 生命周期钩子
onMounted(() => {
  window.addEventListener("keydown", handleKeyDown)

  // 注册全局方法，用于外部调用
  window.PftDebug = {
    show: () => toggleDebugDialog(true),
    hide: () => toggleDebugDialog(false),
    toggle: toggleDebugDialog,
    addData: addCustomData,
    clearData: clearCustomData,
  }
})

onUnmounted(() => {
  window.removeEventListener("keydown", handleKeyDown)

  // 清理全局方法
  if (window.PftDebug) {
    window.PftDebug = undefined
  }
})

// 对外暴露方法
defineExpose({
  show: () => toggleDebugDialog(true),
  hide: () => toggleDebugDialog(false),
  toggle: toggleDebugDialog,
  addCustomData,
  clearCustomData,
})
</script>

<script lang="ts">
// 为Typescript声明全局方法
declare global {
  interface Window {
    PftDebug?: {
      show: () => void
      hide: () => void
      toggle: (visible?: boolean) => void
      addData: (key: string, value: any) => void
      clearData: () => void
    }
  }
}

export default {
  name: "PftDebug",
}
</script>

<style lang="scss">
.pft-debug {
}
.debug-content {
  max-height: 60vh;
  overflow-y: auto;
}

.debug-section {
  margin-bottom: 16px;

  h3 {
    margin-top: 0;
    margin-bottom: 8px;
    font-size: 16px;
    color: var(--el-text-color-primary, #303133);
    padding-bottom: 8px;
  }
}

.debug-item {
  display: flex;
  margin-bottom: 8px;
  background-color: var(--el-fill-color-light, #ebeef575);
  padding: 5px 10px;
  border-radius: 3px;

  .label {
    min-width: 130px;
    font-weight: bold;
    color: var(--el-text-color-regular, #606266);
  }

  .value {
    flex: 1;
    word-break: break-all;
    color: var(--el-text-color-primary, #343434);
  }
}

.debug-footer {
  display: flex;
  justify-content: flex-end;
}
</style>
